/**
 * Quick Stats Grid Component
 * Displays financial metrics and trends in a grid layout
 * Following PRD Phase 2 specifications (lines 249-253)
 */

import React from 'react';
import { useDashboardStore } from '../../stores/dashboardStore';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { cn } from '../../lib/utils';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Activity,
  FileText,
  Minus,
  RefreshCw,
  Loader2
} from 'lucide-react';

interface StatCardProps {
  title: string;
  value: string | number;
  trend: number;
  description: string;
  icon: React.ElementType;
  color: string;
  isLoading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  trend,
  description,
  icon: Icon,
  color,
  isLoading = false
}) => {
  const formatValue = (val: string | number) => {
    if (typeof val === 'number') {
      if (title.toLowerCase().includes('balance') || title.toLowerCase().includes('revenue')) {
        return new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(val);
      }
      return val.toLocaleString();
    }
    return val;
  };

  const getTrendIcon = () => {
    if (trend > 0) return TrendingUp;
    if (trend < 0) return TrendingDown;
    return Minus;
  };

  const getTrendColor = () => {
    if (trend > 0) return 'text-green-600';
    if (trend < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getTrendText = () => {
    const absValue = Math.abs(trend);
    if (trend > 0) return `+${absValue.toFixed(1)}% from last month`;
    if (trend < 0) return `-${absValue.toFixed(1)}% from last month`;
    return 'No change from last month';
  };

  const TrendIcon = getTrendIcon();

  return (
    <Card className="transition-all duration-200 hover:shadow-md">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className={cn("h-4 w-4", color)} />
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {isLoading ? (
            <div className="flex items-center space-x-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <div className="text-2xl font-bold text-muted-foreground">Loading...</div>
            </div>
          ) : (
            <div className="text-2xl font-bold">
              {formatValue(value)}
            </div>
          )}
          
          <div className="flex items-center space-x-1">
            <TrendIcon className={cn("h-3 w-3", getTrendColor())} />
            <p className={cn("text-xs", getTrendColor())}>
              {getTrendText()}
            </p>
          </div>
          
          <p className="text-xs text-muted-foreground">
            {description}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

const QuickStatsGrid: React.FC = () => {
  const { stats, isLoading, error, fetchStats, lastUpdated } = useDashboardStore();
  const { toast } = useToast();

  const handleRefresh = async () => {
    try {
      await fetchStats();
      toast({
        title: "Stats refreshed",
        description: "Dashboard statistics have been updated",
      });
    } catch (error) {
      toast({
        title: "Refresh failed",
        description: "Unable to refresh dashboard statistics",
        variant: "destructive",
      });
    }
  };

  const getLastUpdatedText = () => {
    if (!lastUpdated) return 'Never updated';
    
    // Robust date handling - handle both Date objects and date strings
    let dateObj: Date;
    
    try {
      if (lastUpdated instanceof Date) {
        // Already a Date object
        dateObj = lastUpdated;
      } else if (typeof lastUpdated === 'string') {
        // Convert string to Date
        dateObj = new Date(lastUpdated);
        
        // Check if the date is valid
        if (isNaN(dateObj.getTime())) {
          return 'Invalid date';
        }
      } else if (typeof lastUpdated === 'number') {
        // Handle timestamp
        dateObj = new Date(lastUpdated);
        
        if (isNaN(dateObj.getTime())) {
          return 'Invalid timestamp';
        }
      } else {
        // Unknown type
        return 'Invalid date format';
      }
    } catch (error) {
      // Handle any parsing errors
      return 'Date parsing error';
    }
    
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffMins < 1) return 'Just updated';
    if (diffMins < 60) return `Updated ${diffMins} minutes ago`;
    if (diffHours < 24) return `Updated ${diffHours} hours ago`;
    
    // Format time in IST (Indian Standard Time)
    return `Updated ${dateObj.toLocaleTimeString('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit'
    })}`;
  };

  // Default stats when no data is available
  const defaultStats = {
    cashBalance: 0,
    monthlyRevenue: 0,
    totalTransactions: 0,
    pendingInvoices: 0,
    cashBalanceTrend: 0,
    revenueTrend: 0,
    transactionsTrend: 0,
    invoicesTrend: 0
  };

  const currentStats = stats || defaultStats;

  const statCards = [
    {
      title: 'Cash Balance',
      value: currentStats.cashBalance,
      trend: currentStats.cashBalanceTrend,
      description: 'Current account balance',
      icon: DollarSign,
      color: 'text-green-600'
    },
    {
      title: 'Monthly Revenue',
      value: currentStats.monthlyRevenue,
      trend: currentStats.revenueTrend,
      description: 'Revenue for current month',
      icon: TrendingUp,
      color: 'text-blue-600'
    },
    {
      title: 'Transactions',
      value: currentStats.totalTransactions,
      trend: currentStats.transactionsTrend,
      description: 'Total transactions recorded',
      icon: Activity,
      color: 'text-purple-600'
    },
    {
      title: 'Pending Invoices',
      value: currentStats.pendingInvoices,
      trend: currentStats.invoicesTrend,
      description: 'Invoices awaiting payment',
      icon: FileText,
      color: 'text-orange-600'
    }
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Quick Statistics</h2>
          <p className="text-sm text-muted-foreground">
            {getLastUpdatedText()}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          <span className="ml-2">Refresh</span>
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <FileText className="h-4 w-4 text-red-600" />
              <div>
                <p className="text-sm text-red-800 font-medium">Failed to load statistics</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <StatCard
            key={stat.title}
            title={stat.title}
            value={stat.value}
            trend={stat.trend}
            description={stat.description}
            icon={stat.icon}
            color={stat.color}
            isLoading={isLoading}
          />
        ))}
      </div>

      {/* Additional Insights */}
      {stats && !isLoading && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Financial Insights</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Cash Flow Analysis</h4>
                <p className="text-sm text-muted-foreground">
                  {currentStats.cashBalanceTrend > 0 
                    ? "Your cash balance is trending upward. Good financial health!"
                    : currentStats.cashBalanceTrend < 0
                    ? "Cash balance is declining. Consider reviewing expenses."
                    : "Cash balance is stable. Monitor for any changes."
                  }
                </p>
              </div>
              
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Revenue Performance</h4>
                <p className="text-sm text-muted-foreground">
                  {currentStats.revenueTrend > 5 
                    ? "Excellent revenue growth! Keep up the momentum."
                    : currentStats.revenueTrend > 0
                    ? "Steady revenue growth. Consider scaling opportunities."
                    : "Revenue needs attention. Review sales strategies."
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default QuickStatsGrid;